import {
  Bell,
  BookOpen,
  BrainCircuit,
  CalendarDays,
  FlaskConical,
  Inbox,
  Landmark,
  LayoutDashboard,
  MessageSquare,
  MoreHorizontal,
  PieChart,
  Settings,
  WalletCards,
  Wand2,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { cn } from "@/lib/utils";

const primaryItems = [
  { label: "Overview", path: "/dashboard", icon: LayoutDashboard, accent: "from-orizonte-lime to-orizonte-emerald" },
  { label: "Subscriptions", path: "/subscriptions", icon: WalletCards, accent: "from-emerald-300 to-orizonte-lime" },
  { label: "Decision Inbox", path: "/decision-inbox", icon: Inbox, accent: "from-orizonte-lime to-yellow-100" },
  { label: "Intelligence", path: "/intelligence", icon: BrainCircuit, accent: "from-orizonte-lime to-cyan-300" },
  { label: "Analytics", path: "/analytics", icon: PieChart, accent: "from-cyan-300 to-orizonte-emerald" },
];

const sectionItems = [
  ...primaryItems,
  { label: "Future Stack", path: "/future-stack", icon: Wand2, accent: "from-purple-300 to-orizonte-lime" },
  { label: "Timeline", path: "/calendar", icon: CalendarDays, accent: "from-yellow-200 to-orizonte-lime" },
  { label: "Scenarios", path: "/scenarios", icon: FlaskConical, accent: "from-blue-300 to-orizonte-lime" },
  { label: "Accounts", path: "/accounts", icon: Landmark, accent: "from-emerald-200 to-teal-400" },
  { label: "Vision", path: "/vision", icon: BookOpen, accent: "from-white to-orizonte-lime" },
  { label: "Settings", path: "/settings", icon: Settings, accent: "from-orizonte-lime to-yellow-100" },
];

export const MobileBottomNav = () => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const protectedRoute = sectionItems.some((item) => location.pathname === item.path) || location.pathname === "/admin/readiness";
  if (!protectedRoute) return null;

  const closeExpanded = () => setIsExpanded(false);
  const openNotifications = () => {
    closeExpanded();
    document.dispatchEvent(new CustomEvent("open-notifications"));
  };

  return (
    <>
      <AnimatePresence>
        {isExpanded && (
          <>
            <motion.button
              type="button"
              aria-label="Close section navigation"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeExpanded}
              className="fixed inset-0 z-[58] bg-black/35 backdrop-blur-[2px] md:hidden"
            />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-x-3 bottom-[88px] z-[61] rounded-2xl border border-white/10 bg-[#040705]/95 p-3 shadow-2xl shadow-black/40 backdrop-blur-2xl md:hidden"
            >
              <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-orizonte-lime/70 to-transparent" />
              <div className="grid grid-cols-5 gap-2">
                {sectionItems.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={item.label}
                      aria-label={item.label}
                      onClick={closeExpanded}
                      className={cn(
                        "relative flex aspect-square items-center justify-center rounded-2xl border transition-all duration-200",
                        active
                          ? "border-orizonte-lime/50 bg-orizonte-lime/10 text-orizonte-lime shadow-[0_0_22px_rgba(204,255,0,0.16)]"
                          : "border-white/[0.06] bg-white/[0.025] text-orizonte-textMuted hover:border-white/15 hover:bg-white/[0.05] hover:text-orizonte-text"
                      )}
                    >
                      <span className={cn("absolute inset-x-3 top-2 h-px rounded-full bg-gradient-to-r opacity-60", item.accent)} />
                      <item.icon className="h-5 w-5" />
                      {active && <span className="absolute bottom-2 h-1 w-1 rounded-full bg-orizonte-lime" />}
                    </Link>
                  );
                })}
                <button
                  type="button"
                  title="Alerts"
                  aria-label="Alerts"
                  onClick={openNotifications}
                  className="relative flex aspect-square items-center justify-center rounded-2xl border border-white/[0.06] bg-white/[0.025] text-orizonte-textMuted transition-all duration-200 hover:border-white/15 hover:bg-white/[0.05] hover:text-orizonte-text"
                >
                  <span className="absolute inset-x-3 top-2 h-px rounded-full bg-gradient-to-r from-red-300 to-orizonte-lime opacity-60" />
                  <Bell className="h-5 w-5" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <nav className="fixed inset-x-3 bottom-3 z-[60] rounded-[1.4rem] border border-white/10 bg-[#040705]/92 p-1.5 shadow-2xl shadow-black/50 backdrop-blur-2xl md:hidden">
        <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-orizonte-lime/60 to-transparent" />
        <div className="grid grid-cols-6 items-center gap-1">
          {primaryItems.slice(0, 2).map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={item.label}
                aria-label={item.label}
                className={cn(
                  "relative flex h-12 items-center justify-center rounded-2xl transition-all duration-200",
                  active ? "bg-orizonte-lime/10 text-orizonte-lime" : "text-orizonte-textMuted hover:bg-white/[0.04] hover:text-orizonte-text"
                )}
              >
                <item.icon className="h-5 w-5" />
                {active && <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-orizonte-lime" />}
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => document.dispatchEvent(new CustomEvent("open-ai-chat"))}
            className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-orizonte-lime to-orizonte-emerald text-black shadow-[0_0_24px_rgba(204,255,0,0.28)]"
            aria-label="Open AI chat"
            title="AI chat"
          >
            <MessageSquare className="h-5 w-5" />
          </button>
          {primaryItems.slice(2, 4).map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={item.label}
                aria-label={item.label}
                className={cn(
                  "relative flex h-12 items-center justify-center rounded-2xl transition-all duration-200",
                  active ? "bg-orizonte-lime/10 text-orizonte-lime" : "text-orizonte-textMuted hover:bg-white/[0.04] hover:text-orizonte-text"
                )}
              >
                <item.icon className="h-5 w-5" />
                {active && <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-orizonte-lime" />}
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setIsExpanded((open) => !open)}
            className={cn(
              "relative flex h-12 items-center justify-center rounded-2xl transition-all duration-200",
              isExpanded ? "bg-orizonte-lime/10 text-orizonte-lime" : "text-orizonte-textMuted hover:bg-white/[0.04] hover:text-orizonte-text"
            )}
            aria-label={isExpanded ? "Close section navigation" : "Open section navigation"}
            aria-expanded={isExpanded}
            title={isExpanded ? "Close" : "Sections"}
          >
            {isExpanded ? <X className="h-5 w-5" /> : <MoreHorizontal className="h-5 w-5" />}
          </button>
        </div>
      </nav>
    </>
  );
};
